/* eslint-disable react-refresh/only-export-components */
import { useMutation, useQuery } from 'urql';
import { graphql } from '../../gql';
import React, { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Button } from '@progress/kendo-react-buttons';
import { Grid, GridColumn, GridNoRecords } from '@progress/kendo-react-grid';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Input } from '@progress/kendo-react-inputs';
import { PageTitle } from '../PageTitle.tsx';
import { Fieldset } from '../form-components/Fieldset.tsx';
import { HeroIcon } from '../../shared/HeroIcon.tsx';

interface SignUpFormsPageProps {
  companyId: string;
}

export const CompanySignUpFormsQuery = graphql(/* GraphQL */ `
  query companySignUpForms($companyId: String!) {
    companySignUpForms(companyId: $companyId) {
      id
      name
      isDefault
      signUpCustomFields {
        id
      }
    }
  }
`);

export const CreateSignUpFormMutation = graphql(/* GraphQL */ `
  mutation createSignUpForm($companyId: String!, $name: String!) {
    createSignUpForm(companyId: $companyId, name: $name) {
      id
      name
      isDefault
    }
  }
`);

export const UpdateSignUpFormMutation = graphql(/* GraphQL */ `
  mutation updateSignUpForm(
    $companyId: String!
    $formId: String!
    $name: String!
  ) {
    updateSignUpForm(companyId: $companyId, formId: $formId, name: $name)
  }
`);

export const DeleteSignUpFormMutation = graphql(/* GraphQL */ `
  mutation deleteSignUpForm($companyId: String!, $formId: String!) {
    deleteSignUpForm(companyId: $companyId, formId: $formId)
  }
`);

export const SignUpFormsPage: React.FC<SignUpFormsPageProps> = ({
  companyId,
}) => {
  const navigate = useNavigate();

  const [{ data, fetching }, reloadForms] = useQuery({
    query: CompanySignUpFormsQuery,
    variables: { companyId },
    pause: companyId === undefined || companyId === null,
  });

  const forms = [...(data?.companySignUpForms ?? [])].sort(
    (a, b) =>
      Number(b.isDefault) - Number(a.isDefault) || a.name.localeCompare(b.name),
  );

  const [, createSignUpForm] = useMutation(CreateSignUpFormMutation);
  const [, deleteSignUpForm] = useMutation(DeleteSignUpFormMutation);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogName, setDialogName] = useState('');

  const openCreateDialog = () => {
    setDialogName('');
    setDialogOpen(true);
  };
  const closeDialog = () => setDialogOpen(false);

  const onDialogSubmit = async () => {
    const name = dialogName.trim();
    if (!name) return;
    const res = await createSignUpForm({ companyId, name });
    reloadForms({ requestPolicy: 'network-only' });
    const newId = res.data?.createSignUpForm?.id;
    closeDialog();
    if (newId) {
      navigate({
        to: '/admin/sign-up-forms/$formId',
        params: { formId: newId },
      });
    }
  };

  const onDeleteForm = async (form: { id: string; name: string }) => {
    if (
      !window.confirm(
        `Formular "${form.name}" wirklich löschen? Seminare ohne anderes Formular nutzen wieder das Standard-Formular.`,
      )
    ) {
      return;
    }
    await deleteSignUpForm({ companyId, formId: form.id });
    reloadForms({ requestPolicy: 'network-only' });
  };

  return (
    <>
      <PageTitle title="Anmeldeformulare" />

      <div className={'mb-4 flex justify-end'}>
        <Button
          themeColor="primary"
          className="[&>span.k-button-text]:flex [&>span.k-button-text]:items-center [&>span.k-button-text]:gap-1"
          onClick={openCreateDialog}
        >
          <HeroIcon name="Plus" className="block h-5 w-5" />
          <div>Neues Formular</div>
        </Button>
      </div>

      {dialogOpen && (
        <Dialog
          appendTo={document.getElementById('root')}
          autoFocus={true}
          width="min(480px, 100vw)"
          title="Neues Formular anlegen"
          onClose={closeDialog}
        >
          <div className="flex flex-col gap-4">
            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium">Name</span>
              <Input
                value={dialogName}
                onChange={(e) => setDialogName(String(e.value ?? ''))}
                placeholder="Name des Formulars"
                autoFocus
              />
            </label>
            <div className="flex flex-row gap-2">
              <Button
                themeColor="primary"
                disabled={!dialogName.trim()}
                onClick={(e) => {
                  e.preventDefault();
                  void onDialogSubmit();
                }}
              >
                Speichern
              </Button>
              <Button
                themeColor="secondary"
                onClick={(e) => {
                  e.preventDefault();
                  closeDialog();
                }}
              >
                Abbrechen
              </Button>
            </div>
          </div>
        </Dialog>
      )}

      <div className="k-form k-form-md relative flex w-full flex-col rounded-md bg-white shadow lg:w-full">
        <Fieldset
          legend={`Anmeldeformulare`}
          description="Hier können mehrere Anmeldeformulare verwaltet werden. In den Einstellungen eines Seminars kann festgelegt werden, welches Formular verwendet wird. Ohne Auswahl gilt das Standard-Formular."
        >
          <div className="flex flex-col gap-4 md:col-span-9 [&_.k-form-field]:!mt-0">
            <Grid
              className="[&_.k-grid-norecords-template]:border-0"
              data={forms}
              showLoader={fetching}
            >
              <GridNoRecords>
                {fetching ? '' : 'Keine Formulare gefunden'}
              </GridNoRecords>
              <GridColumn field="name" title="Name" />
              <GridColumn
                title="Felder"
                width="100px"
                cells={{
                  data: (props) => (
                    <td>{props.dataItem.signUpCustomFields.length}</td>
                  ),
                }}
              />
              <GridColumn
                title="Standard"
                width="120px"
                cells={{
                  data: (props) => (
                    <td>
                      {props.dataItem.isDefault && (
                        <HeroIcon
                          name="Check"
                          className="block h-5 w-5"
                          aria-label="Standard-Formular"
                        />
                      )}
                    </td>
                  ),
                }}
              />
              <GridColumn
                title="Aktionen"
                width="120px"
                cells={{
                  data: (props) => (
                    <td className={'k-button-link-secondary flex space-x-2'}>
                      <Link
                        to="/admin/sign-up-forms/$formId"
                        params={{ formId: props.dataItem.id }}
                        className="k-button k-button-md k-button-outline k-button-outline-secondary k-rounded-md !text-[#6c757d] hover:!text-white"
                        title="Formularfelder bearbeiten"
                      >
                        <HeroIcon name="Pencil" className="block h-5 w-5" />
                      </Link>
                      <button
                        className={`k-button k-button-md k-button-outline k-button-outline-secondary k-rounded-md ${
                          props.dataItem.isDefault
                            ? '!text-zinc-300'
                            : '!text-[#6c757d] hover:!text-white'
                        }`}
                        onClick={() => onDeleteForm(props.dataItem)}
                        disabled={props.dataItem.isDefault}
                        title={
                          props.dataItem.isDefault
                            ? 'Standard-Formular kann nicht gelöscht werden'
                            : 'Formular löschen'
                        }
                      >
                        <HeroIcon name="Trash" className="block h-5 w-5" />
                      </button>
                    </td>
                  ),
                }}
              />
            </Grid>
          </div>
        </Fieldset>
      </div>
    </>
  );
};
